<template>
    <div v-for="(group, groupIndex) in formData.groupingProducts" :key="`group-${group.productType}`">
        <v-checkbox
            v-model="group.flagGroupProducts"
            hide-details
            :label="$t(`productType.${group.productType}`)"
            :data-cy="group.productType"
            @update:model-value="resetGroupValues(group, groupIndex)"
        />
        <div v-if="group.flagGroupProducts" class="pb-4 pl-6">
            <v-radio-group
                v-model="group.bestPriceOffersFilterStrategy"
                hide-details
                @update:model-value="resetValues(group, groupIndex)"
            >
                <v-radio
                    v-for="{value, title} in groupingProductsVariants(group.productType)"
                    :key="value"
                    :label="title"
                    :value="value"
                    :data-cy="value"
                />
            </v-radio-group>
            <div v-if="group.productType === 'flight'">
                <v-checkbox
                    v-model="group.offersWithBestProfitFilterEnabled"
                    hide-details
                    :label="$t('show_the_most_profitable_offers_for_TO')"
                    :disabled="group.bestPriceOffersFilterStrategy !== 'ONLY_CHEAPEST'"
                    data-cy="offers-with-best-profit-filter-enabled"
                />
            </div>
            <div v-if="group.productType === 'accommodation'">
                <div class="text-subtitle-1 pt-3">{{ $t('show_description_of_hotels_group_as') }}:</div>
                <v-radio-group v-model="group.groupHeaderDisplayingStrategy" hide-details>
                    <v-radio
                        v-for="{value, title} in useGroupHeaderDisplayingStrategy"
                        :key="value"
                        :label="title"
                        :value="value"
                        :data-cy="value"
                    />
                </v-radio-group>
                <template v-if="isTO1">
                    <v-checkbox
                        v-model="group.ownProductDynamicPricingEnabled"
                        hide-details
                        :label="$t('adjust_markup_for_selfoperated_products')"
                        :data-cy="group.productType"
                    />
                    <div class="text-body-1 d-flex gc-2 align-center flex-wrap pb-2">
                        <div class="d-flex gc-2 align-center flex-wrap">
                            <div>- {{ $t('adjustMarkupForSelfoperatedProducts.first_part_one') }}</div>
                            <v-number-input
                                v-model="group.ownProductDynamicPriceDifference"
                                :min="0"
                                hide-details
                                :label="
                                    group.ownProductDynamicPriceDifferenceInCurrency
                                        ? `${$t('amount')}`
                                        : `${$t('percent')}`
                                "
                                class="flex-grow-0 flex-shrink-0"
                                width="180"
                                data-cy="own-product-dynamic-price-difference"
                            >
                                <template v-slot:append-inner>
                                    <v-btn
                                        size="x-small"
                                        color="primary"
                                        class="cursor-pointer"
                                        variant="text"
                                        :icon="
                                            group.ownProductDynamicPriceDifferenceInCurrency
                                                ? 'mdi-cash'
                                                : 'mdi-percent'
                                        "
                                    />
                                    <v-btn
                                        size="x-small"
                                        color="grey-lighten-1"
                                        class="cursor-pointer"
                                        variant="text"
                                        :icon="
                                            group.ownProductDynamicPriceDifferenceInCurrency
                                                ? 'mdi-percent'
                                                : 'mdi-cash'
                                        "
                                        data-cy="own-product-dynamic-price-difference-cursor-btn"
                                        @click="
                                            group.ownProductDynamicPriceDifferenceInCurrency =
                                                !group.ownProductDynamicPriceDifferenceInCurrency
                                        "
                                    />
                                </template>
                            </v-number-input>
                        </div>
                        <div>{{ $t('adjustMarkupForSelfoperatedProducts.first_part_two') }}</div>
                    </div>
                    <div class="text-body-1 pb-2 d-flex gc-2 align-center flex-wrap">
                        <div>- {{ $t('adjustMarkupForSelfoperatedProducts.second_part') }}</div>
                        <v-number-input
                            v-model="group.ownProductDynamicPriceAdjustment"
                            :min="0"
                            hide-details
                            :label="
                                group.ownProductDynamicPriceDifferenceInCurrency
                                    ? `${$t('amount')}`
                                    : `${$t('percent')}`
                            "
                            class="flex-grow-0 flex-shrink-0"
                            width="180"
                            data-cy="own-product-dynamic-price-adjustment"
                        >
                            <template v-slot:append-inner>
                                <v-btn
                                    size="x-small"
                                    color="primary"
                                    class="cursor-pointer"
                                    variant="text"
                                    :icon="
                                        group.ownProductDynamicPriceAdjustmentInCurrency ? 'mdi-cash' : 'mdi-percent'
                                    "
                                />
                                <v-btn
                                    size="x-small"
                                    color="grey-lighten-1"
                                    class="cursor-pointer"
                                    variant="text"
                                    :icon="
                                        group.ownProductDynamicPriceAdjustmentInCurrency ? 'mdi-percent' : 'mdi-cash'
                                    "
                                    data-cy="own-product-dynamic-price-adjustment-cursor-btn"
                                    @click="
                                        group.ownProductDynamicPriceAdjustmentInCurrency =
                                            !group.ownProductDynamicPriceAdjustmentInCurrency
                                    "
                                />
                            </template>
                        </v-number-input>
                    </div>
                    <div class="text-body-1 pb-2">
                        <div>- {{ $t('adjustMarkupForSelfoperatedProducts.third_part') }}</div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    const formData = defineModel<any>()
    const {t} = useI18n()

    const groupingProductsVariants = productType => {
        if (productType === 'flight') {
            return [
                {value: 'ALL', title: t('groupingProductsVariants.ALL')},
                {value: 'ONLY_CHEAPEST', title: t('groupingProductsVariants.ONLY_CHEAPEST')},
            ]
        }
        return [
            {value: 'ALL', title: t('groupingProductsVariants.ALL')},
            {value: 'ONLY_CHEAPEST', title: t('groupingProductsVariants.ONLY_CHEAPEST')},
            {value: 'ONLY_SELFOPERATED', title: t('groupingProductsVariants.ONLY_SELFOPERATED')},
        ]
    }

    const useGroupHeaderDisplayingStrategy = computed(() => {
        return [
            {
                value: 'BEST_PRICE_OFFER_DESCRIPTION',
                title: t('groupHeaderDisplayingStrategy.BEST_PRICE_OFFER_DESCRIPTION'),
            },
            {
                value: 'OWN_PRODUCT_DESCRIPTION_IF_EXISTS',
                title: t('groupHeaderDisplayingStrategy.OWN_PRODUCT_DESCRIPTION_IF_EXISTS'),
            },
            {
                value: 'OWN_PRODUCT_DESCRIPTION_ONLY',
                title: t('groupHeaderDisplayingStrategy.OWN_PRODUCT_DESCRIPTION_ONLY'),
            },
        ]
    })

    const resetGroupValues = (group, groupIndex) => {
        if (!group.flagGroupProducts) {
            formData.value.groupingProducts[groupIndex] = {
                productType: group.productType,
                bestPriceOffersFilterStrategy: 'ALL',
                flagGroupProducts: false,
                groupHeaderDisplayingStrategy: 'BEST_PRICE_OFFER_DESCRIPTION',
                offersWithBestProfitFilterEnabled: false,
                ownProductDynamicPriceAdjustment: null,
                ownProductDynamicPriceAdjustmentInCurrency: false,
                ownProductDynamicPriceDifference: null,
                ownProductDynamicPriceDifferenceInCurrency: false,
                ownProductDynamicPricingEnabled: false,
            }
        }
    }
    const resetValues = (group, groupIndex) => {
        if (group.productType === 'flight' || group.productType === 'accommodation') {
            formData.value.groupingProducts[groupIndex] = {
                productType: group.productType,
                bestPriceOffersFilterStrategy: group.bestPriceOffersFilterStrategy,
                flagGroupProducts: group.flagGroupProducts,
                groupHeaderDisplayingStrategy: 'BEST_PRICE_OFFER_DESCRIPTION',
                offersWithBestProfitFilterEnabled: false,
                ownProductDynamicPriceAdjustment: null,
                ownProductDynamicPriceAdjustmentInCurrency: false,
                ownProductDynamicPriceDifference: null,
                ownProductDynamicPriceDifferenceInCurrency: false,
                ownProductDynamicPricingEnabled: false,
            }
        }
    }

    const isTO1 = computed(() => {
        return authStore.isTO1
    })
</script>
